import React, { useMemo, useState } from 'react';
import { SearchIcon, ChevronDown, X } from 'lucide-react';
import { Protocol, PROTOCOLS } from '../data/protocols';
interface SidebarProps {
  activeProtocolId: string | null;
  onSelectProtocol: (protocol: Protocol) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}
type SearchField = 'all' | 'name' | 'category' | 'tcmPattern' | 'clinical' | 'points';
const SEARCH_FIELD_LABELS: Record<SearchField, string> = {
  all: 'All Fields',
  name: 'Protocol Name',
  category: 'Category',
  tcmPattern: 'TCM Pattern',
  clinical: 'Clinical Picture',
  points: 'Acupoints'
};
// Helper to check if a protocol matches the search query in a specific field
function matchesField(protocol: Protocol, query: string, field: SearchField): boolean {
  const q = query.toLowerCase();
  switch (field) {
    case 'name':
      return protocol.name.toLowerCase().includes(q);
    case 'category':
      return protocol.category.toLowerCase().includes(q);
    case 'tcmPattern':
      return protocol.tcmPattern.toLowerCase().includes(q);
    case 'clinical':
      return protocol.indicationSummary.toLowerCase().includes(q) || protocol.remarks.toLowerCase().includes(q);
    case 'points':
      return protocol.points.some(p => p.points.toLowerCase().includes(q) || p.pointSet.toLowerCase().includes(q) || p.note.toLowerCase().includes(q));
    case 'all':
    default:
      return protocol.name.toLowerCase().includes(q) || protocol.category.toLowerCase().includes(q) || protocol.tcmPattern.toLowerCase().includes(q) || protocol.indicationSummary.toLowerCase().includes(q) || protocol.remarks.toLowerCase().includes(q) || protocol.points.some(p => p.points.toLowerCase().includes(q) || p.pointSet.toLowerCase().includes(q) || p.note.toLowerCase().includes(q));
  }
}
// Get which fields matched for display
function getMatchedFields(protocol: Protocol, query: string): string[] {
  if (!query) return [];
  const q = query.toLowerCase();
  const matched: string[] = [];
  if (protocol.name.toLowerCase().includes(q)) matched.push('name');
  if (protocol.category.toLowerCase().includes(q)) matched.push('category');
  if (protocol.tcmPattern.toLowerCase().includes(q)) matched.push('TCM pattern');
  if (protocol.indicationSummary.toLowerCase().includes(q)) matched.push('clinical');
  if (protocol.remarks.toLowerCase().includes(q)) matched.push('remarks');
  if (protocol.points.some(p => p.points.toLowerCase().includes(q))) matched.push('points');
  if (protocol.points.some(p => p.pointSet.toLowerCase().includes(q))) matched.push('point set');
  return matched;
}
export function Sidebar({
  activeProtocolId,
  onSelectProtocol,
  searchQuery,
  onSearchChange
}: SidebarProps) {
  const [searchField, setSearchField] = useState<SearchField>('all');
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  // Group protocols by category with enhanced search
  const {
    groupedProtocols,
    matchInfo,
    totalMatches
  } = useMemo(() => {
    const filtered = PROTOCOLS.filter(p => matchesField(p, searchQuery, searchField));
    // Build match info for highlighting
    const matchInfo: Record<string, string[]> = {};
    if (searchQuery) {
      filtered.forEach(p => {
        matchInfo[p.id] = getMatchedFields(p, searchQuery);
      });
    }
    const groups: Record<string, Protocol[]> = {};
    filtered.forEach(p => {
      if (!groups[p.category]) {
        groups[p.category] = [];
      }
      groups[p.category].push(p);
    });
    // Sort categories alphabetically
    const sortedGroups = Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {} as Record<string, Protocol[]>);
    return {
      groupedProtocols: sortedGroups,
      matchInfo,
      totalMatches: filtered.length
    };
  }, [searchQuery, searchField]);
  const placeholderText = searchField === 'all' ? 'Search conditions, points, patterns…' : `Search by ${SEARCH_FIELD_LABELS[searchField].toLowerCase()}…`;
  return <aside className="w-full md:w-[280px] bg-white border-b md:border-b-0 md:border-r border-[#E5E7EB] p-4 overflow-y-auto md:h-[calc(100vh-60px)] flex-shrink-0" data-id="element-82">
      <h2 className="text-[0.95rem] font-semibold mb-3 text-[#8B1538]" data-id="element-83">
        Protocols & Point Sets
      </h2>

      {/* Search Field Selector */}
      <div className="relative mb-2" data-id="element-84">
        <button onClick={() => setShowFieldDropdown(!showFieldDropdown)} className="w-full flex items-center justify-between px-3 py-1.5 text-xs bg-[#FEF2F2] border border-[#FECACA] rounded text-[#8B1538] hover:bg-[#FEE2E2] transition-colors" data-id="element-85">
          <span data-id="element-86">
            Search in: <strong data-id="element-87">{SEARCH_FIELD_LABELS[searchField]}</strong>
          </span>
          <ChevronDown className="h-3.5 w-3.5" data-id="element-88" />
        </button>

        {showFieldDropdown && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20" data-id="element-89">
            {(Object.keys(SEARCH_FIELD_LABELS) as SearchField[]).map(field => <button key={field} onClick={() => {
          setSearchField(field);
          setShowFieldDropdown(false);
        }} className={`w-full text-left px-3 py-2 text-sm hover:bg-[#FEF2F2] transition-colors ${searchField === field ? 'bg-[#FEF2F2] text-[#8B1538] font-medium' : 'text-gray-700'}`} data-id="element-90">
                  {SEARCH_FIELD_LABELS[field]}
                </button>)}
          </div>}
      </div>

      {/* Search Input */}
      <div className="relative mb-3" data-id="element-91">
        <input type="text" placeholder={placeholderText} value={searchQuery} onChange={e => onSearchChange(e.target.value)} className="w-full pl-8 pr-8 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1538]/20 focus:border-[#8B1538] transition-all" data-id="element-92" />
        <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" data-id="element-93" />
        {searchQuery && <button onClick={() => onSearchChange('')} className="absolute right-2.5 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-94">
            <X className="h-4 w-4" data-id="element-95" />
          </button>}
      </div>

      {/* Search Results Count */}
      {searchQuery && <div className="mb-3 px-2 py-1.5 bg-[#FEF2F2] rounded text-xs text-[#8B1538]" data-id="element-96">
          Found <strong data-id="element-97">{totalMatches}</strong> protocol
          {totalMatches !== 1 ? 's' : ''} matching "{searchQuery}"
          {searchField !== 'all' && <span data-id="element-98"> in {SEARCH_FIELD_LABELS[searchField].toLowerCase()}</span>}
        </div>}

      {/* Quick Search Suggestions */}
      {!searchQuery && <div className="mb-3 flex flex-wrap gap-1.5" data-id="element-99">
          {['stroke', 'pain', 'ST36', 'liver', 'kidney'].map(term => <button key={term} onClick={() => onSearchChange(term)} className="px-2 py-0.5 text-[0.7rem] bg-gray-100 text-gray-600 rounded-full hover:bg-[#FEF2F2] hover:text-[#8B1538] transition-colors" data-id="element-100">
              {term}
            </button>)}
        </div>}

      {/* Protocol List */}
      <div className="space-y-4" data-id="element-101">
        {Object.entries(groupedProtocols).map(([category, protocols]) => <div key={category} className="category-block" data-id="element-102">
            <div className="text-[0.75rem] uppercase text-gray-500 font-semibold mb-1.5 tracking-wide flex items-center gap-2" data-id="element-103">
              <span data-id="element-104">{category}</span>
              <span className="text-[0.65rem] font-normal bg-gray-100 px-1.5 py-0.5 rounded-full" data-id="element-105">
                {protocols.length}
              </span>
            </div>
            <ul className="space-y-0.5" data-id="element-106">
              {protocols.map(protocol => {
            const matches = matchInfo[protocol.id] || [];
            const isActive = activeProtocolId === protocol.id;
            return <li key={protocol.id} onClick={() => onSelectProtocol(protocol)} className={`
                      px-2.5 py-2 text-[0.85rem] rounded-md cursor-pointer transition-all duration-200
                      ${isActive ? 'bg-[#8B1538] text-white font-medium shadow-sm' : 'text-gray-700 hover:bg-[#FEF2F2] hover:text-[#8B1538]'}
                    `} data-id="element-107">
                    <div className="font-medium leading-tight" data-id="element-108">
                      {protocol.name}
                    </div>
                    {/* Show matched fields when searching */}
                    {searchQuery && matches.length > 0 && !isActive && <div className="mt-1 flex flex-wrap gap-1" data-id="element-109">
                        {matches.slice(0, 3).map(field => <span key={field} className="text-[0.6rem] px-1.5 py-0.5 bg-[#FECACA]/50 text-[#8B1538] rounded" data-id="element-110">
                            {field}
                          </span>)}
                        {matches.length > 3 && <span className="text-[0.6rem] text-gray-500" data-id="element-111">
                            +{matches.length - 3} more
                          </span>}
                      </div>}
                  </li>;
          })}
            </ul>
          </div>)}

        {Object.keys(groupedProtocols).length === 0 && <div className="text-center py-8" data-id="element-112">
            <div className="text-gray-400 mb-2" data-id="element-113">
              <SearchIcon className="h-8 w-8 mx-auto" data-id="element-114" />
            </div>
            <p className="text-sm text-gray-500 mb-1" data-id="element-115">No protocols found</p>
            <p className="text-xs text-gray-400" data-id="element-116">
              Try searching for "{searchQuery}" in a different field
            </p>
            {searchField !== 'all' && <button onClick={() => setSearchField('all')} className="mt-2 text-xs text-[#8B1538] hover:underline" data-id="element-117">
                Search all fields instead
              </button>}
          </div>}
      </div>
    </aside>;
}